import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Eye, EyeOff, ArrowUpCircle } from 'lucide-react';
import { Modal } from '../../components/common/Modal';
import { cuppingSessionService } from '../../services/cuppingSessionService';
import { profileService } from '../../services/profileService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { CuppingSessionTabs } from './components/CuppingSessionTabs';
import { CuppingSessionItemForm, type CuppingSessionItemFormHandle } from './components/CuppingSessionItemForm';
import { useLanguage } from '../../features/language/useLanguage';
import { workOrderService } from '../../services/workOrderService';
import { WorkOrderRoastViewer } from '../../components/workOrder/WorkOrderRoastViewer';
import { useOnboardingStore } from '../../store/onboardingStore';

export const CuppingSessionFormPage: React.FC = () => {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeItemId, setActiveItemId] = useState('');
  const [showReveal, setShowReveal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dirtyItemIds, setDirtyItemIds] = useState<Set<string>>(new Set());
  const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const formRefs = useRef<Record<string, CuppingSessionItemFormHandle | null>>({});
  const onboardingStep4 = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 4,
  );

  const { data: session, isLoading: isLoadingSession } = useQuery({
    queryKey: ['cupping-session', id],
    queryFn: () => cuppingSessionService.getById(id || ''),
    enabled: !!id,
  });

  const { data: profilesData, isLoading: isLoadingProfiles } = useQuery({
    queryKey: ['cupping-profiles'],
    queryFn: () => profileService.getAllCuppingProfiles(),
  });
  
  const { data: selectedWorkOrder, isLoading: isLoadingWO } = useQuery({
    queryKey: ['work-order', selectedWorkOrderId],
    queryFn: () => workOrderService.getById(selectedWorkOrderId || ''),
    enabled: !!selectedWorkOrderId,
  });

  const profiles = useMemo(() => {
    return Array.isArray(profilesData) ? profilesData : [];
  }, [profilesData]);

  const details = useMemo(() => {
    return (session?.details || []).slice().sort((a, b) => a.orderIndex - b.orderIndex);
  }, [session]);

  const resolvedActiveItemId = activeItemId || details[0]?.inventoryItemId || '';

  const detailMap = useMemo(() => {
    return new Map(details.map((detail) => [detail.inventoryItemId, detail]));
  }, [details]);

  const detailInventoryIds = useMemo(() => new Set(details.map((detail) => detail.inventoryItemId)), [details]);

  const relevantProfiles = useMemo(() => {
    if (!session) return [];
    return profiles.filter((profile) => {
      const inventoryId = profile.inventory_item_id || '';
      if (!detailInventoryIds.has(inventoryId)) return false;
      if (profile.cupping_session_id && profile.cupping_session_id !== session.id) return false;
      return true;
    });
  }, [profiles, detailInventoryIds, session]);

  const profileMap = useMemo(() => {
    return new Map(relevantProfiles.map((profile) => [profile.inventory_item_id || '', profile]));
  }, [relevantProfiles]);

  const isComplete = session?.status === 'Complete';

  const completedIds = useMemo(() => {
    if (isComplete) {
      return new Set(details.map((detail) => detail.inventoryItemId));
    }
    return new Set(dirtyItemIds);
  }, [details, dirtyItemIds, isComplete]);

  const sessionMode = (session?.cuppingMode as 'Full' | 'Simplified' | 'SCA') || 'Simplified';

  const activeDetail = detailMap.get(resolvedActiveItemId) || details[0];

  const completeMutation = useMutation({
    mutationFn: (sessionId: string) => cuppingSessionService.complete(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cupping-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['cupping-session', id] });
      // Onboarding step 4 finishes once the practice cupping is scored and
      // completed — tell the overlay to advance to step 5.
      if (onboardingStep4) {
        window.dispatchEvent(new CustomEvent('qrema:cupping-created', { detail: { cuppingId: id } }));
      }
      navigate('/cupping-sessions', { replace: true });
    },
  });

  useEffect(() => {
    if (!id) {
      navigate('/cupping-sessions', { replace: true });
    }
  }, [id, navigate]);

  const handleTabChange = (itemId: string) => {
    setActiveItemId(itemId);
    setErrorMessage('');
  };


  const handleClose = () => {
    setErrorMessage('');
    navigate('/cupping-sessions', { replace: true });
  };

  const handleCompleteSessionClick = () => {
    if (!session) return;
    const missing = details.filter((detail) => !completedIds.has(detail.inventoryItemId));
    if (missing.length > 0) {
      setErrorMessage(t('cuppingSessions.form.errors.completeAllTabs'));
      return;
    }
    setShowCompleteConfirmModal(true);
  };

  const handleConfirmComplete = async () => {
    if (!session) return;
    setShowCompleteConfirmModal(false);
    await Promise.all(
      details.map((detail) => {
        const ref = formRefs.current[detail.inventoryItemId];
        return ref ? ref.submit() : Promise.resolve();
      }),
    );
    completeMutation.mutate(session.id);
  };

  const handleSavedProfile = () => {
    setErrorMessage('');
  };

  const handleWorkOrderClick = (workOrderId: string) => {
    setSelectedWorkOrderId(workOrderId);
  };

  const handleDirtyChange = useCallback((inventoryItemId: string, isDirty: boolean) => {
    setDirtyItemIds((prev) => {
      const has = prev.has(inventoryItemId);
      if (isDirty && has) return prev;
      if (!isDirty && !has) return prev;

      const next = new Set(prev);
      if (isDirty) next.add(inventoryItemId);
      else next.delete(inventoryItemId);
      return next;
    });
  }, []);

  const handleScrollTop = () => {
    const container = document.querySelector('main');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoadingSession || isLoadingProfiles) {
    return <LoadingSpinner text={t('cuppingSessions.form.loadingSession')} />;
  }

  if (!session || !activeDetail) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        {t('cuppingSessions.form.notFound')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-test-id="cupping-session-back"
            onClick={handleClose}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{session.code}</h1>
            <p className="text-sm text-gray-500">{t('cuppingSessions.columns.cupper')}: {session.cupperName || t('settingsMcp.values.none')}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm text-gray-600">
            {t('forms.common.status')}: <span className="font-semibold">{session.status}</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-700">
              {session.blindMode ? t('cuppingSessions.form.blindModeEnabled') : t('cuppingSessions.form.standardMode')}
            </p>
            {session.blindMode && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{t('cuppingSessions.form.clickToViewDetails')}</span>
                <button
                  data-test-id="cupping-session-hold-to-view"
                  onClick={() => setShowReveal((prev) => !prev)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
                >
                  {showReveal ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  <span>{t('cuppingSessions.form.reveal')}</span>
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="text-xs text-gray-500">
              {t('cuppingSessions.form.completedCount', { completed: completedIds.size, total: details.length })}
            </div>
          </div>
        </div>

        {session.blindMode && showReveal && activeDetail && (
          <div
            className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 cursor-pointer hover:bg-gray-100 transition-shadow shadow-sm"
            data-test-id="cupping-session-reveal-details"
            onClick={() => activeDetail.workOrderId && handleWorkOrderClick(activeDetail.workOrderId)}
          >
            <div className="font-semibold text-gray-800">{activeDetail.workOrderNumber}</div>
            <div>{activeDetail.itemName}</div>
            <div className="text-xs text-gray-500 uppercase">{activeDetail.roastType} roast</div>
          </div>
        )}
      </div>

      {details.map((detail) => {
        const isActive = detail.inventoryItemId === resolvedActiveItemId;
        const profile = profileMap.get(detail.inventoryItemId) || undefined;
        return (
          <div key={detail.inventoryItemId} className={isActive ? '' : 'hidden'}>
            <CuppingSessionItemForm
              ref={(instance) => {
                formRefs.current[detail.inventoryItemId] = instance;
              }}
              detail={detail}
              sessionId={session.id}
              profile={profile}
              readOnly={isComplete ? true : !isActive}
              blindMode={session.blindMode}
              onSaved={isComplete ? undefined : handleSavedProfile}
              onDirtyChange={isComplete ? undefined : handleDirtyChange}
              onWorkOrderClick={handleWorkOrderClick}
              sessionMode={sessionMode}
              isActive={isActive}
              // Onboarding step 4: drop the per-section lock so the practice
              // cupper can drag a score slider immediately (no "Unlock Section").
              lockSections={!onboardingStep4}
              topTabs={
                <CuppingSessionTabs
                  details={details}
                  activeId={resolvedActiveItemId}
                  onChange={handleTabChange}
                  blindMode={session.blindMode}
                  completedIds={completedIds}
                  dataTestId="cupping-session-tabs-top"
                />
              }
              bottomTabs={
                <CuppingSessionTabs
                  details={details}
                  activeId={resolvedActiveItemId}
                  onChange={handleTabChange}
                  blindMode={session.blindMode}
                  completedIds={completedIds}
                  dataTestId="cupping-session-tabs-bottom"
                />
              }
            />
          </div>
        );
      })}

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {errorMessage && (
          <div className="text-sm text-red-600" data-test-id="cupping-session-form-error">
            {errorMessage}
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            data-test-id="cupping-session-close-button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('forms.actions.close')}
          </button>
          {!isComplete && (
            <button
              type="button"
              data-test-id="cupping-session-complete-button"
              onClick={handleCompleteSessionClick}
              disabled={completeMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {completeMutation.isPending ? t('forms.actions.saving') : t('cuppingSessions.form.completeSession')}
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Complete Session */}
      <Modal
        isOpen={showCompleteConfirmModal}
        onClose={() => setShowCompleteConfirmModal(false)}
        title={t('cuppingSessions.form.confirmCompleteTitle')}
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <svg className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('cuppingSessions.form.profilesLockedTitle')}</p>
              <p className="text-xs text-amber-700 mt-1">
                {t('cuppingSessions.form.profilesLockedMessage')}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCompleteConfirmModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {t('auth.common.cancel')}
            </button>
            <button
              type="button"
              data-test-id="cupping-session-complete-confirm"
              onClick={handleConfirmComplete}
              disabled={completeMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {completeMutation.isPending ? t('forms.actions.saving') : t('purchaseOrder.form.modals.proceed')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Read-Only Work Order Roast Profile Modal */}
      <Modal
        isOpen={!!selectedWorkOrderId}
        onClose={() => setSelectedWorkOrderId(null)}
        title={t('cuppingSessions.form.roastViewer.title')}
        maxWidth="7xl"
      >
        {isLoadingWO ? (
          <LoadingSpinner text={t('workOrders.form.loading')} />
        ) : selectedWorkOrder ? (
          <WorkOrderRoastViewer workOrder={selectedWorkOrder} />
        ) : (
          <div className="p-6 text-center text-gray-500">
            {t('cuppingSessions.form.roastViewer.workOrderNotFound')}
          </div>
        )}
      </Modal>

      <button
        data-test-id="cupping-session-back-to-top"
        onClick={handleScrollTop}
        type="button"
        className="fixed bottom-6 right-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700"
      >
        <ArrowUpCircle className="h-5 w-5" />
      </button>
    </div>
  );
};
